// frontend/src/components/common/ErrorBoundary.js
import React from 'react';
import { AlertTriangle, RefreshCw, Home, Mail } from 'lucide-react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  logErrorToService = (error, errorInfo) => {
    // Integration with error tracking services like Sentry, LogRocket, etc.
    try {
      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        errorId: this.state.errorId
      };
      
      // Send to your error tracking service
      // Example: Sentry.captureException(error, { extra: errorData });
      console.log('Error logged:', errorData);
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { fallback: CustomFallback, showDetails = false } = this.props;
      
      // Use custom fallback if provided
      if (CustomFallback) {
        return (
          <CustomFallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onReload={this.handleReload}
            onGoHome={this.handleGoHome}
          />
        );
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-surface-page flex items-center justify-center px-4">
          <div className="max-w-md w-full">
            <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-danger-bg mb-4">
                <AlertTriangle className="h-8 w-8 text-danger" />
              </div>
              
              <h1 className="text-xl font-semibold text-ink-soft mb-2">
                Oops! Something went wrong
              </h1>
              
              <p className="text-ink-muted mb-6">
                We're sorry, but something unexpected happened. Our team has been notified.
              </p>

              {showDetails && this.state.error && (
                <div className="bg-surface-page rounded-lg p-4 mb-6 text-left">
                  <h3 className="text-sm font-medium text-ink-soft mb-2">Error Details:</h3>
                  <div className="text-small text-ink-muted font-mono break-all">
                    <div className="mb-2">
                      <span className="font-semibold">Message:</span> {this.state.error.message}
                    </div>
                    <div className="mb-2">
                      <span className="font-semibold">Error ID:</span> {this.state.errorId}
                    </div>
                    {this.state.error.stack && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-ink-soft hover:text-ink-soft">
                          Stack Trace
                        </summary>
                        <pre className="mt-2 text-small overflow-auto max-h-32 bg-white p-2 rounded border">
                          {this.state.error.stack}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={this.handleRetry}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 flex items-center justify-center px-4 py-2 border border-border-default text-sm font-medium rounded-md text-ink-soft bg-white hover:bg-surface-page focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </button>
                  
                  <button
                    onClick={this.handleReload}
                    className="flex-1 flex items-center justify-center px-4 py-2 border border-border-default text-sm font-medium rounded-md text-ink-soft bg-white hover:bg-surface-page focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reload Page
                  </button>
                </div>
                
                <a
                  href="mailto:support@partsapp.com?subject=Error Report&body=Error ID: {{errorId}}"
                  className="inline-flex items-center text-sm text-ink-muted hover:text-ink-soft"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Report this issue
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC to wrap components with error boundary
export const withErrorBoundary = (Component, errorBoundaryConfig = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryConfig}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Async error boundary for handling async errors
export class AsyncErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AsyncErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidMount() {
    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleUnhandledRejection = (event) => {
    // Suppress browser extension message channel errors (non-critical)
    if (event.reason && event.reason.message && event.reason.message.includes('message channel closed before a response was received')) {
      console.warn('Browser extension promise rejection suppressed:', event.reason.message);
      event.preventDefault();
      return;
    }
    
    this.setState({
      hasError: true,
      error: new Error(event.reason)
    });
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-danger-bg border border-danger rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger mr-2" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-danger">
                An error occurred
              </h3>
              <p className="text-sm text-danger mt-1">
                {this.state.error?.message || 'Something went wrong'}
              </p>
            </div>
            <button
              onClick={this.handleRetry}
              className="ml-3 bg-danger-bg px-3 py-1 rounded text-sm text-danger hover:bg-danger-bg"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for handling errors in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const resetError = () => setError(null);

  const captureError = (error) => {
    console.error('Error captured:', error);
    setError(error);
  };

  // Throw error to be caught by error boundary
  if (error) {
    throw error;
  }

  return { captureError, resetError };
};

// Custom error types
export class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

export class ValidationError extends Error {
  constructor(message, errors) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

export class NetworkError extends Error {
  constructor(message) {
    super(message);
    this.name = 'NetworkError';
  }
}